/**
 * App mode resolution for the dual-deployment strategy:
 * - 'tb-site'       -> toolsandbubbles.com (existing production, untouched)
 * - 'saas-platform' -> serviceapp.ai (SaaS marketing site + platform)
 *
 * Resolution order:
 * 1. Hostname (serviceapp.ai) — wins at runtime so one build can serve both domains
 * 2. VITE_APP_MODE env var (set per Vercel project at build time)
 * 3. Default: 'tb-site'
 */
import { isSaasPlatformHost, isToolsAndBubblesHost } from '../share/serviceappBrand.js';

export type AppMode = 'tb-site' | 'saas-platform';

export function getAppMode(): AppMode {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (isToolsAndBubblesHost(host)) return 'tb-site';
    if (isSaasPlatformHost(host)) return 'saas-platform';
  }

  // `import.meta.env` is `undefined` under a Node serverless runtime (this module
  // is reachable from /api/saas via shared util imports). Guard so the access does
  // not throw there; the `&&` keeps Vite's static env replacement for the browser.
  const envMode = (import.meta.env && (import.meta.env.VITE_APP_MODE as string | undefined))?.trim();
  if (envMode === 'saas-platform') return 'saas-platform';
  if (envMode === 'tb-site') return 'tb-site';

  return 'tb-site';
}

export function isSaasPlatform(): boolean {
  return getAppMode() === 'saas-platform';
}

/** Dashboard + tenant APIs: use client RLS on ServiceApp hosts (not the global service-role API). */
export function shouldUseSaasClientDataLayer(): boolean {
  if (typeof window !== 'undefined' && isSaasPlatformHost(window.location.hostname)) {
    return true;
  }
  return isSaasPlatform();
}

/** Overview metrics via Supabase session + RLS (toolsandbubbles.com has no /api/* on Vercel). */
export function shouldUseClientDashboardOverview(): boolean {
  if (shouldUseSaasClientDataLayer()) return true;
  return getAppMode() === 'tb-site';
}

/** HR compliance + registers: toolsandbubbles.com has no Vercel /api/hr route in production. */
export function shouldUseClientHrCompliance(): boolean {
  return shouldUseClientDashboardOverview();
}

/** Staff list endpoints: prefer Supabase client on tb-site when API routes 404. */
export function shouldPreferClientStaffLists(): boolean {
  return getAppMode() === 'tb-site';
}
